import {Component, HostBinding, ViewChild} from '@angular/core';
import {AccountService} from '../shared/services/account.service';
import {combineLatest, Subscription} from 'rxjs';
import {CategoryEnum, User} from '../shared/models/account.model';
import {I18nKeyEnum} from '../shared/models/language.model';
import {IconEnum} from '../shared/models/assets.model';
import {CrawlerService} from '../shared/services/crawler.service';
import {ValidatorsErrorEnum} from '../shared/models/form.model';
import {FormControl, FormGroup, NgForm} from '@angular/forms';
import {
    CallScenario,
    SummaryElement, voiceBotAges, voiceBotGenders,
    voiceBotLanguages,
    VoiceBotType
} from '../shared/models/voice-bot.model';
import {VoiceBotService} from '../shared/services/voice-bot.service';
import {I18nPipe} from '../shared/pipes/i18n.pipe';
import {FreeTrialService} from '../shared/services/free-trial.service';

@Component({
    selector: 'app-setup-account',
    standalone: false,
    templateUrl: './setup-account.component.html',
    styleUrl: './setup-account.component.scss'
})

export class SetupAccountComponent {
    private crawlingStatusInterval: any | undefined;
    protected readonly I18nKeyEnum = I18nKeyEnum;
    protected readonly ICONS = IconEnum;
    protected readonly validatorsErrorEnum = ValidatorsErrorEnum;
    @HostBinding('class.setup-account') default = true;
    @HostBinding('class.hide') hideControl = true;
    @ViewChild('callScenarioForm') callScenarioForm: NgForm | undefined;
    @ViewChild('callScenarioPropertyForm') callScenarioPropertyForm: NgForm | undefined;
    public subscriptions = new Subscription();
    public websiteCrawlingStatus: number = 0;
    public user: User | undefined;
    public greetingCtrl = new FormControl();
    public voiceBotLanguageCtrl = new FormControl();
    public voiceBotGenderCtrl = new FormControl();
    public voiceBotAgeCtrl = new FormControl();
    public voiceBotLanguages = voiceBotLanguages;
    public voiceBotGenders = voiceBotGenders;
    public voiceBotAges = voiceBotAges;
    public voiceBotTypeCtrl = new FormControl();
    public callScenarioCtrl = new FormControl();
    public PropertyCtrl = new FormControl();
    voiceBotTypes: VoiceBotType[] = [];
    public callScenarios: CallScenario[] = [];
    public userAccountFormGroup = new FormGroup({
        greeting: this.greetingCtrl,
        voiceBotType: this.voiceBotTypeCtrl,
        voiceBotLanguage: this.voiceBotLanguageCtrl,
        voiceBotGender: this.voiceBotGenderCtrl,
        voiceBotAge: this.voiceBotAgeCtrl,
    });
    
    public callScenarioFormGroup = new FormGroup({
        callScenario: this.callScenarioCtrl
    });
    public addPropertyFormGroup = new FormGroup({
        property: this.PropertyCtrl
    });
    
    public categoryOpenState = {
        general: true,
        callScenarios: false,
        knowledgeBase: false,
    };
    
    public categoryDone = {
        general: false,
        callScenarios: false,
        knowledgeBase: false
    };
    
    public summary: SummaryElement[] | undefined;
    // TODO store voiceTypes
    public voiceBotAudio: HTMLAudioElement | undefined;
    public callScenarioExample = '';
    public inProgress = {
        accountCreation: true,
        callReasonCreation: false,
        callReasonAdjustment: false
    }
    
    constructor(private accountService: AccountService,
                private crawlerService: CrawlerService,
                private freeTrialService: FreeTrialService,
                private voiceBotService: VoiceBotService,
                private readonly i18nPipe: I18nPipe) {
        this.initSubscriptions();
        this.initWebsiteCrawler();
    }
    
    public saveGeneralSettings() {
       if (!this.greetingCtrl.value || !this.user?.phoneService.phoneServiceId) {
            return;
        }
        this.accountService.saveGreeting(this.greetingCtrl.value);
        this.voiceBotService.saveType(this.voiceBotTypeCtrl.value.voice_id, this.user?.phoneService.phoneServiceId);
    }
    
    public saveCallScenario() {
        this.callScenarioFormGroup.markAllAsTouched();
        this.callScenarioFormGroup.updateValueAndValidity();
        if (this.callScenarioFormGroup.invalid || !this.user?.phoneService?.phoneServiceId) {
            return;
        }
        this.inProgress.callReasonCreation = true;
        this.voiceBotService.createCallScenario$(this.user?.phoneService?.phoneServiceId, this.callScenarioCtrl.value)
            .subscribe((callScenario) => {
                this.callScenarios.push(callScenario);
                setTimeout(() => {
                    this.callScenarioForm?.resetForm();
                }, 200);
                this.categoryDone.callScenarios = true;
                this.inProgress.callReasonCreation = false;
            });
    }
    
    deleteCallScenario(callScenario: CallScenario) {
        if (this.inProgress.callReasonCreation ||this.inProgress.callReasonAdjustment) {
            return;
        }
        
        if (!this.user?.phoneService?.phoneServiceId) {
            console.log('deleting call scenario failed: no phoneservice id.');
            return;
        }
        const psId = this.user.phoneService.phoneServiceId;
        const toDeleteToolId = callScenario.tool_id;
        this.inProgress.callReasonAdjustment = true;
        this.voiceBotService.deleteScenario$(psId, callScenario).subscribe({
            next: (res) => {
                this.callScenarios = this.callScenarios.filter(cs => cs.tool_id !== toDeleteToolId);
                console.log('deleted', toDeleteToolId, this.callScenarios);
                this.inProgress.callReasonAdjustment = false;
            },
            error: (err) => {
                console.error(`callReason deletion failed. ${err}`);
                this.inProgress.callReasonAdjustment = false;
            }
        });
    }
    
    
    addProperty(callScenario: CallScenario, text: string, callScenarioCtrl: FormControl<any>) {
        if (!this.user?.phoneService?.phoneServiceId) {
            console.log('adding property failed: no phoneservice id.');
            return;
        }
        const psId = this.user.phoneService.phoneServiceId;
        this.inProgress.callReasonAdjustment = true;
        callScenarioCtrl.setErrors(null);
        this.voiceBotService.addProperty$(psId, callScenario, text).subscribe({
            next: (updatedCallScenario) => {
                console.log(callScenario, updatedCallScenario);
                if (this.voiceBotService.callScenariosAreEqual(callScenario, updatedCallScenario)) {
                    // TODO show warning -> rephrase
                    callScenarioCtrl.setErrors({[ValidatorsErrorEnum.EQUAL]: true});
                    console.log('setError - equal', callScenarioCtrl);
                    this.inProgress.callReasonAdjustment = false;
                    return;
                }
                
                console.log('updated call scenario', updatedCallScenario);
                const i = this.callScenarios.findIndex(cs => cs.tool_id === updatedCallScenario.tool_id);
                if (i >= 0) {
                    this.callScenarios[i].llm_function = updatedCallScenario.llm_function;
                    this.callScenarios[i].function_definition = updatedCallScenario.function_definition;
                }
                this.inProgress.callReasonAdjustment = false;
                setTimeout(() => {
                    this.callScenarioPropertyForm?.resetForm();
                }, 200);
            },
            error: (err) => {
                console.error(`add property failed. ${err}`);
            }
        });
    }
    
    deletePropertyScenario(callScenario: CallScenario, propertyName: string) {
        if (this.inProgress.callReasonAdjustment || this.inProgress.callReasonCreation) {
            return;
        }
        if (!this.user?.phoneService?.phoneServiceId) {
            console.log('deleting Call scenario property failed: no phoneservice id.');
            return;
        }
        const psId = this.user.phoneService.phoneServiceId;
        this.inProgress.callReasonAdjustment = true;
        this.voiceBotService.deleteCallScenarioProperty$(psId, callScenario, propertyName)
            .subscribe({
                next: (updatedCallScenario) => {
                    const index = this.callScenarios.findIndex(cr => cr.tool_id === updatedCallScenario.tool_id);
                    if (index >= 0) {
                        this.callScenarios[index].llm_function = updatedCallScenario.llm_function;
                        this.callScenarios[index].function_definition = updatedCallScenario.function_definition;
                        console.log('property deleted', this.callScenarios);
                    }
                    this.inProgress.callReasonAdjustment = false;
                },
                error: (err) => {
                    console.error(`callReason property deletion failed. ${err}`);
                    this.inProgress.callReasonAdjustment = false;
                }
            });
    }
    
    public playAudio(voiceBotType: VoiceBotType): void {
        if (!!voiceBotType.preview_url) {
            if (this.voiceBotAudio) {
                this.voiceBotAudio.pause();
                this.voiceBotAudio = undefined;
            }
            
            this.voiceBotAudio = new Audio(voiceBotType.preview_url);
            this.voiceBotAudio.play();
        }
    }
    
    private initForms() {
        this.voiceBotLanguageCtrl.setValue(voiceBotLanguages[1]); // TODO follow language$
        this.voiceBotGenderCtrl.setValue(voiceBotGenders[0]);
        this.voiceBotAgeCtrl.setValue(voiceBotAges[0]);
        this.i18nPipe.transform(I18nKeyEnum.DEFAULT_GREETING).subscribe({
            next: (res) => {
                this.greetingCtrl.setValue(res);
            }
        });
        this.i18nPipe.transform(I18nKeyEnum.CALL_SCENARIO_EXAMPLE).subscribe({
            next: (res) => {
                this.callScenarioExample = res;
            }
        });
        this.voiceBotService.fetchVoiceBotTypes(voiceBotLanguages[0].shortName);
    }
    
    private initSubscriptions() {
        
        
        this.subscriptions.add(this.freeTrialService.freeTrialInitialized$.subscribe((state) => {
            this.hideControl = !state;
            if (state) {
                this.initForms();
            }
        }));
        
        this.subscriptions.add(combineLatest([
            this.accountService.greetingSaved$,
            this.voiceBotService.voiceBotTypesSaved$
        ]).subscribe((data) => {
                if (!!data[0] && !!data[1]) {
                    this.categoryDone.general = true;
                    this.categoryOpenState.general = false;
                    this.categoryOpenState.callScenarios = true;
                }
            }
        ));
        
        
        
        this.subscriptions.add(this.accountService.user$.subscribe((user) => {
            this.user = user;
            this.inProgress.accountCreation = false;
        }));
        
        this.accountService.openCategory$.subscribe((category) => {
            this.openCategory(category);
        });
        
        this.voiceBotService.testCallTriggered$.subscribe((res) => {
            if (res) {
                this.closeCategories();
            }
        });
        
        this.subscriptions.add(this.voiceBotService.voiceBotTypes$.subscribe((voiceBotTypes) => {
            this.initVoiceBotTypes(voiceBotTypes);
        }));
        
        
        this.subscriptions.add(this.crawlerService.summary$.subscribe((summary) => {
            this.summary = summary;
            this.websiteCrawlingStatus = 100;
            this.categoryDone.knowledgeBase = !!summary;
        }));
        
        this.subscriptions.add(this.crawlerService.activeCrawler$.subscribe(active => {
            if (active) {
                this.initWebsiteCrawler();
                return;
            }
        }));
    }
    
    private initWebsiteCrawler(): void {
        this.crawlingStatusInterval = setInterval(() => {
            this.websiteCrawlingStatus += this.websiteCrawlingStatus < 90 ? 1 : 0.05;
            if (this.websiteCrawlingStatus === 99.95) {
                this.clearCrawlingStatus();
            }
        }, 1000);
    }
    
    private clearCrawlingStatus() {
        clearInterval(this.crawlingStatusInterval);
    }
    
    private initVoiceBotTypes(voiceBotTypes: VoiceBotType[]) {
        this.voiceBotTypes = voiceBotTypes;
        this.voiceBotTypeCtrl.setValue(this.voiceBotTypes[0]);
    }
    
    private closeCategories() {
    this.categoryOpenState = {
            general: false,
            callScenarios: false,
            knowledgeBase: false,
        };
    }
    
    private openCategory(category: CategoryEnum) {
        this.categoryOpenState.callScenarios = false;
        this.categoryOpenState.knowledgeBase = false;
        this.categoryOpenState.general = false;
        switch (category) {
            case CategoryEnum.GENERAL: {
                this.categoryOpenState.general = true;
                break;
            }
            case CategoryEnum.KNOWLEDGEABLE: {
                this.categoryOpenState.knowledgeBase = true;
            }
        }
    }
}
